import React, { useEffect, useMemo, useState } from "react";
import { geoMercator, geoPath } from "d3-geo";
import { feature } from "topojson-client";
import { ChevronLeft, ChevronRight, Globe, GitCommitHorizontal } from "lucide-react";

const HIGHLIGHT_IDS = new Set(["840", "276", "392", "826", "528", "158", "410", "484"]);

export const CHIP_STOPS = [
  {
    n: 1, code: "us", flag: "🇺🇸", coords: [-82.06, 35.92],
    country: { it: "Stati Uniti", en: "United States" },
    place: "Spruce Pine, North Carolina",
    stage: { it: "Quarzo ad altissima purezza", en: "Ultra-high-purity quartz" },
    companies: "Sibelco, The Quartz Corp",
    stat: { it: "Un solo distretto per i crogioli di tutto il mondo", en: "One mining district supplies crucibles worldwide" },
    text: {
      it: "Dalle miniere di Spruce Pine esce il quarzo purissimo per i crogioli in cui fonderà il silicio. Quando nel settembre 2024 l'uragano Helene fermò le miniere per due settimane, l'intera industria trattenne il fiato.",
      en: "The ultra-pure quartz for the crucibles in which silicon is melted comes out of the Spruce Pine mines. When Hurricane Helene shut the mines for two weeks in September 2024, the whole industry held its breath.",
    },
  },
  {
    n: 2, code: "de", flag: "🇩🇪", coords: [12.83, 48.17], offset: [34, 26], label: [18, 5, "start"],
    country: { it: "Germania", en: "Germany" },
    place: "Burghausen, Baviera",
    stage: { it: "Polisilicio a \"undici nove\"", en: "Eleven-nines polysilicon" },
    companies: "Wacker Chemie (con Hemlock, USA e Tokuyama, Giappone) · Zeiss · Siltronic",
    stat: { it: "Purezza 99,999999999%", en: "99.999999999% purity" },
    text: {
      it: "Con il processo Siemens, Wacker, Hemlock e Tokuyama trasformano il silicio metallurgico in polisilicio di grado elettronico. La Germania mette anche gli specchi Zeiss degli scanner EUV e i wafer di Siltronic (12% del mercato).",
      en: "Using the Siemens process, Wacker, Hemlock and Tokuyama turn metallurgical silicon into electronic-grade polysilicon. Germany also supplies the Zeiss mirrors inside EUV scanners and Siltronic's wafers (12% of the market).",
    },
  },
  {
    n: 3, code: "jp", flag: "🇯🇵", coords: [140.21, 37.13], offset: [30, -36], label: [-16, 5, "end"],
    country: { it: "Giappone", en: "Japan" },
    place: "Shirakawa (Fukushima), Imari, Yonezawa",
    stage: { it: "Cristallo Czochralski e wafer da 300 mm", en: "Czochralski crystal and 300 mm wafers" },
    companies: "Shin-Etsu, SUMCO · Tokyo Electron · resine fotosensibili",
    stat: { it: "Shin-Etsu 28% + SUMCO 25% dei wafer; 38% della capacità 300 mm", en: "Shin-Etsu 28% + SUMCO 25% of wafers; 38% of 300 mm capacity" },
    text: {
      it: "Il polisilicio viene fuso a oltre 1.400 gradi e tirato in lingotti monocristallini da 300 millimetri, poi affettati in dischi spessi meno di un millimetro. Nel 2011 il terremoto del Tōhoku fermò Shirakawa e circa un quinto dell'offerta mondiale.",
      en: "Polysilicon is melted above 1,400 °C and pulled into 300 mm single-crystal ingots, then sliced into wafers less than a millimetre thick. In 2011 the Tōhoku earthquake halted Shirakawa and roughly a fifth of world supply.",
    },
  },
  {
    n: 4, code: "gb", flag: "🇬🇧", coords: [0.12, 52.21], offset: [-34, -30], label: [-18, 5, "end"],
    country: { it: "Regno Unito", en: "United Kingdom" },
    place: "Cambridge",
    stage: { it: "Architettura Arm per la CPU", en: "Arm architecture for the CPU" },
    companies: "Arm · (Nvidia, Synopsys e Cadence a Santa Clara)",
    stat: { it: "EDA: Synopsys 31%, Cadence 30%, Siemens EDA 13%", en: "EDA: Synopsys 31%, Cadence 30%, Siemens EDA 13%" },
    text: {
      it: "Nvidia disegna il chip a Santa Clara con il software di progettazione di Synopsys e Cadence; la CPU che affianca l'acceleratore usa l'architettura Arm, progettata a Cambridge. La California disegna, ma con strumenti altrui.",
      en: "Nvidia designs the chip in Santa Clara with Synopsys and Cadence design software; the CPU paired with the accelerator uses the Arm architecture, designed in Cambridge. California draws the blueprint, but with other people's tools.",
    },
  },
  {
    n: 5, code: "nl", flag: "🇳🇱", coords: [5.4, 51.42], offset: [10, -40], label: [18, 5, "start"],
    country: { it: "Paesi Bassi", en: "Netherlands" },
    place: "Veldhoven",
    stage: { it: "Litografia EUV", en: "EUV lithography" },
    companies: "ASML (ottiche Zeiss) · Applied Materials, Lam, KLA, Tokyo Electron",
    stat: { it: "ASML 100%: fornitore unico al mondo", en: "ASML 100%: the world's sole supplier" },
    text: {
      it: "Gli scanner EUV di ASML sono le uniche macchine capaci di incidere i nodi più avanzati: un monopolio olandese costruito su specchi tedeschi e chimica giapponese. Senza Veldhoven nessuna fonderia avanzata può lavorare.",
      en: "ASML's EUV scanners are the only machines able to pattern the most advanced nodes: a Dutch monopoly built on German mirrors and Japanese chemistry. Without Veldhoven no leading-edge fab can operate.",
    },
  },
  {
    n: 6, code: "tw", flag: "🇹🇼", coords: [120.97, 24.8],
    country: { it: "Taiwan", en: "Taiwan" },
    place: "Hsinchu e Tainan",
    stage: { it: "Fonderia a 3-4 nm e assemblaggio CoWoS", en: "3-4 nm foundry and CoWoS packaging" },
    companies: "TSMC · GlobalWafers · Foxconn, Quanta, Wistron",
    stat: { it: "TSMC 72,5% dei ricavi delle fonderie", en: "TSMC 72.5% of foundry revenue" },
    text: {
      it: "TSMC incide il processore a 4 o 3 nanometri e poi unisce processore e memorie con l'assemblaggio CoWoS, su substrati che impiegano materiali isolanti giapponesi. Una quota crescente della produzione esce dall'Arizona.",
      en: "TSMC patterns the processor at 4 or 3 nanometres and then joins processor and memory with CoWoS packaging, on substrates using Japanese insulating materials. A growing share of output now comes from Arizona.",
    },
  },
  {
    n: 7, code: "kr", flag: "🇰🇷", coords: [127.44, 37.28], label: [-18, 5, "end"],
    country: { it: "Corea del Sud", en: "South Korea" },
    place: "Icheon e Pyeongtaek",
    stage: { it: "Memorie HBM", en: "HBM memory stacks" },
    companies: "SK hynix, Samsung (con Micron da Taiwan e Giappone) · SK Siltron",
    stat: { it: "HBM: SK hynix 50%, Samsung 33%, Micron 18%", en: "HBM: SK hynix 50%, Samsung 33%, Micron 18%" },
    text: {
      it: "SK hynix e Samsung producono le pile di memoria ad alta banda che hanno cambiato le gerarchie del settore: tre produttori per un superciclo dei prezzi, con il quarto incomodo cinese CXMT in crescita.",
      en: "SK hynix and Samsung make the high-bandwidth memory stacks that have reshuffled the industry's hierarchy: three producers behind a price supercycle, with China's CXMT the uninvited fourth.",
    },
  },
  {
    n: 8, code: "mx", flag: "🇲🇽", coords: [-103.35, 20.67],
    country: { it: "Messico", en: "Mexico" },
    place: "Guadalajara, Jalisco",
    stage: { it: "Assemblaggio di schede, armadi e raffreddamento", en: "Board, rack and cooling assembly" },
    companies: "Foxconn, Quanta, Wistron (con Taiwan e Stati Uniti)",
    stat: { it: "L'ultimo anello prima dei data center", en: "The last link before the data centres" },
    text: {
      it: "Foxconn, Quanta e Wistron montano schede, armadi e sistemi di raffreddamento. Da qui l'acceleratore raggiunge i data center di Stati Uniti, Europa, Golfo e Asia, con l'eccezione, per ora, della Cina.",
      en: "Foxconn, Quanta and Wistron assemble boards, racks and cooling systems. From here the accelerator reaches data centres in the United States, Europe, the Gulf and Asia — with the exception, for now, of China.",
    },
  },
];

const DESTINATION = { coords: [-77.49, 39.04], label: { it: "Data center", en: "Data centres" } };

const L = {
  it: { kicker: "Grafico filiera", title: "Il viaggio di un acceleratore, Paese per Paese", sub: "Otto Paesi indispensabili, nessuno sufficiente. Tocca una tappa per aprire la scheda.", map: "Mappa", flow: "Flusso", stage: "Tappa", companies: "Chi", prev: "Precedente", next: "Prossima", loading: "Caricamento mappa…", source: "Fonte: Horizon Brief, «Dal quarzo al chip»" },
  en: { kicker: "Supply-chain chart", title: "An accelerator's journey, country by country", sub: "Eight indispensable countries, none sufficient. Tap a stop to open its card.", map: "Map", flow: "Flow", stage: "Stop", companies: "Who", prev: "Previous", next: "Next", loading: "Loading map…", source: "Source: Horizon Brief, “From quartz to chip”" },
};

const W = 960, H = 440;
const BBOX = { type: "Polygon", coordinates: [[[-128, 8], [152, 8], [152, 62], [-128, 62], [-128, 8]]] };

const WorldMap = ({ lang, active, setActive }) => {
  const [land, setLand] = useState(null);
  useEffect(() => {
    fetch("/world-110m.json").then(r => r.json()).then(topo => setLand(feature(topo, topo.objects.countries).features)).catch(() => setLand([]));
  }, []);
  const { path, projection } = useMemo(() => {
    const projection = geoMercator().fitExtent([[8, 8], [W - 8, H - 8]], BBOX);
    return { path: geoPath(projection), projection };
  }, []);
  const t = L[lang] || L.it;
  if (!land) return <div className="chipmap-loading" data-testid="chipmap-loading">{t.loading}</div>;
  const segs = CHIP_STOPS.slice(1).map((s, i) => ({ from: CHIP_STOPS[i], to: s }));
  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="chipmap-svg" role="img" aria-label={t.title} data-testid="chipmap-svg">
      <defs>
        <marker id="chip-arrow" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="7" markerHeight="7" orient="auto-start-reverse">
          <path d="M0,0 L10,5 L0,10 z" fill="#b45309" />
        </marker>
      </defs>
      <rect width={W} height={H} fill="#eef3f7" />
      {land.map((f, i) => (
        <path key={f.id || `land-${i}`} d={path(f)} className={`chipmap-land ${HIGHLIGHT_IDS.has(f.id) ? "hot" : ""}`} />
      ))}
      {segs.map(({ from, to }) => (
        <path key={`${from.n}-${to.n}`} d={path({ type: "LineString", coordinates: [from.coords, to.coords] })}
          className={`chipmap-route ${active === to.n ? "active" : ""}`} markerEnd="url(#chip-arrow)" />
      ))}
      <path d={path({ type: "LineString", coordinates: [CHIP_STOPS[7].coords, DESTINATION.coords] })} className="chipmap-route dest" markerEnd="url(#chip-arrow)" />
      {(() => { const [x, y] = projection(DESTINATION.coords); return (
        <g transform={`translate(${x},${y})`} className="chipmap-dest">
          <rect x="-6" y="-6" width="12" height="12" />
          <text x="-10" y="-9" textAnchor="end">{DESTINATION.label[lang] || DESTINATION.label.it}</text>
        </g>); })()}
      {CHIP_STOPS.map(s => {
        const [px, py] = projection(s.coords);
        const [ox, oy] = s.offset || [0, 0];
        const x = px + ox, y = py + oy;
        const isActive = active === s.n;
        const [lx, ly, anchor] = s.label || [18, 5, "start"];
        return (
          <g key={s.n} className={`chipmap-stop ${isActive ? "active" : ""}`}
            onClick={() => setActive(s.n)} role="button" tabIndex={0} aria-label={`${s.n}. ${s.country[lang] || s.country.it}`}
            onKeyDown={e => (e.key === "Enter" || e.key === " ") && setActive(s.n)} data-testid={`chipmap-stop-${s.n}`}>
            {s.offset && <><line x1={px} y1={py} x2={x} y2={y} className="chipmap-leader" /><circle cx={px} cy={py} r="2.5" className="chipmap-origin" /></>}
            <g transform={`translate(${x},${y})`}>
              {isActive && <circle r="22" className="chipmap-pulse" />}
              <circle r="12" className="chipmap-dot" />
              <text y="4.5" textAnchor="middle" className="chipmap-num">{s.n}</text>
              <text x={lx} y={ly} textAnchor={anchor} className="chipmap-label">{s.country[lang] || s.country.it}</text>
            </g>
          </g>
        );
      })}
    </svg>
  );
};

const FlowView = ({ lang, active, setActive }) => (
  <ol className="chipflow" data-testid="chipflow">
    {CHIP_STOPS.map((s, i) => (
      <li key={s.n} className="chipflow-item">
        <button type="button" className={`chipflow-step ${active === s.n ? "active" : ""}`} onClick={() => setActive(s.n)} data-testid={`chipflow-step-${s.n}`}>
          <span className="chipflow-flag" aria-hidden="true">{s.flag}</span>
          <span className="chipflow-num">{s.n}</span>
          <span className="chipflow-country">{s.country[lang] || s.country.it}</span>
          <span className="chipflow-stage">{s.stage[lang] || s.stage.it}</span>
        </button>
        {i < CHIP_STOPS.length - 1 && <ChevronRight size={18} className="chipflow-arrow" aria-hidden="true" />}
      </li>
    ))}
  </ol>
);

export const SupplyChainMap = ({ lang = "it", compact = false }) => {
  const [view, setView] = useState("map");
  const [active, setActive] = useState(1);
  const t = L[lang] || L.it;
  const ui = L[lang] ? lang : "it";
  const stop = CHIP_STOPS[active - 1];
  return (
    <section className={`chipmap ${compact ? "compact" : ""}`} data-testid="supply-chain-map" dir="ltr">
      <header className="chipmap-head">
        <div>
          <div className="chipmap-kicker">{t.kicker}</div>
          <h3 className="chipmap-title">{t.title}</h3>
          {!compact && <p className="chipmap-sub">{t.sub}</p>}
        </div>
        <div className="chipmap-toggle" role="tablist">
          <button type="button" role="tab" aria-selected={view === "map"} className={view === "map" ? "active" : ""} onClick={() => setView("map")} data-testid="chipmap-view-map"><Globe size={14} /> {t.map}</button>
          <button type="button" role="tab" aria-selected={view === "flow"} className={view === "flow" ? "active" : ""} onClick={() => setView("flow")} data-testid="chipmap-view-flow"><GitCommitHorizontal size={14} /> {t.flow}</button>
        </div>
      </header>
      {view === "map" ? <WorldMap lang={ui} active={active} setActive={setActive} /> : <FlowView lang={ui} active={active} setActive={setActive} />}
      <div className="chipmap-card" data-testid="chipmap-card" key={active}>
        <div className="chipmap-card-head">
          <span className="chipmap-card-flag" aria-hidden="true">{stop.flag}</span>
          <div>
            <div className="chipmap-card-meta">{t.stage} {stop.n} / {CHIP_STOPS.length} · {stop.place}</div>
            <h4 className="chipmap-card-title" data-testid="chipmap-card-title">{stop.country[ui]} — {stop.stage[ui]}</h4>
          </div>
          <div className="chipmap-card-nav">
            <button type="button" onClick={() => setActive(a => Math.max(1, a - 1))} disabled={active === 1} aria-label={t.prev} data-testid="chipmap-prev"><ChevronLeft size={16} /></button>
            <button type="button" onClick={() => setActive(a => Math.min(CHIP_STOPS.length, a + 1))} disabled={active === CHIP_STOPS.length} aria-label={t.next} data-testid="chipmap-next"><ChevronRight size={16} /></button>
          </div>
        </div>
        <div className="chipmap-card-stat">{stop.stat[ui]}</div>
        <p className="chipmap-card-text">{stop.text[ui]}</p>
        <div className="chipmap-card-companies"><strong>{t.companies}:</strong> {stop.companies}</div>
      </div>
      <div className="chipmap-source">{t.source}</div>
    </section>
  );
};

export default SupplyChainMap;
